import React, { useEffect, useRef } from 'react';

// Cloudflare Turnstile widget — anti-bot signup challenge (ADR-0021, Option B).
//
// Loads the Turnstile script once and renders a managed-challenge widget. Calls
// onToken(token) when solved and onToken('') on expiry/error so the parent can
// gate form submission.
//
// Graceful degradation: when no site key is configured (REACT_APP_TURNSTILE_SITE_KEY
// unset — e.g. local dev, or before CAPTCHA is provisioned) this renders NOTHING
// and never produces a token. The backend CAPTCHA_MODE gate (OFF by default)
// decides whether a missing token is allowed, so the form still works pre-activation.

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'cf-turnstile-script';
const LOAD_TIMEOUT_MS = 10000;

// Resolve once window.turnstile is available, injecting the script on first use.
function ensureTurnstile() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('turnstile_script_failed'));
      document.head.appendChild(script);
    }
    const start = Date.now();
    const poll = setInterval(() => {
      if (window.turnstile) {
        clearInterval(poll);
        resolve();
      } else if (Date.now() - start > LOAD_TIMEOUT_MS) {
        clearInterval(poll);
        reject(new Error('turnstile_load_timeout'));
      }
    }, 50);
  });
}

export default function TurnstileWidget({ siteKey, onToken }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    ensureTurnstile()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
        });
      })
      .catch(() => {
        // Script failed/timed out — report no token; backend mode gate decides.
        if (!cancelled) onToken('');
      });

    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current != null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (_e) {
        // no-op — widget already gone
      }
    };
  }, [siteKey, onToken]);

  if (!siteKey) return null;
  return <div ref={containerRef} />;
}
